import { sha256 } from 'js-sha256'
import TagManager from 'react-gtm-module'
import { GTM_ZOE_DATALAYER } from 'appConfig'

// =====================================================================
// Types — modeled on zoe-website/lib/gtm/typesV2.ts so that quiz-react
// emits events that align with the rest of the marketing site.
// =====================================================================

type Platform = 'nutrition_site'
type Section = 'quiz'
export type LinkPosition = 'header' | 'page_body' | 'footer'
export type ClickType = 'cta' | 'back_button' | 'plan_card'

export type EmailListType = 'nutrition' | 'quiz' | 'menoscale'
export type VideoAction = 'Play' | 'Pause' | 'Complete' | 'Mute' | 'Unmute'
export type MarketingConsent = 'optin' | 'optout'
export type ProductType = 'membership' | 'daily30' | undefined

type GtmEventName =
  | 'quiz_landed'
  | 'quiz_started'
  | 'quiz_screen_viewed'
  | 'quiz_question_answered'
  | 'quiz_completed'
  | 'sign_up'
  | 'user_click'
  | 'video_action'

type BaseGtmEvent = {
  event: GtmEventName
  platform: Platform
  section: Section
  user_id?: string
  quiz_version: string
  product_type?: ProductType
}

export type QuizLandedEvent = BaseGtmEvent & {
  event: 'quiz_landed'
  link_position: LinkPosition
}

export type QuizStartedEvent = BaseGtmEvent & {
  event: 'quiz_started'
  link_position: LinkPosition
}

export type QuizScreenViewedEvent = BaseGtmEvent & {
  event: 'quiz_screen_viewed'
  question_name: string
}

export type QuizQuestionAnsweredEvent = BaseGtmEvent & {
  event: 'quiz_question_answered'
  question_name: string
  question_answer: string | number
}

export type QuizCompletedEvent = BaseGtmEvent & {
  event: 'quiz_completed'
}

export type SignUpEvent = BaseGtmEvent & {
  event: 'sign_up'
  email_list: EmailListType
  hashed_email?: string
  marketing_consent: MarketingConsent
  link_position: LinkPosition
  tag?: string
}

export type UserClickEvent = BaseGtmEvent & {
  event: 'user_click'
  click_type: ClickType
  link_position: LinkPosition
  link_text_image?: string
  page_name?: string
}

export type VideoEvent = BaseGtmEvent & {
  event: 'video_action'
  video_action: VideoAction
  video_name: string
  video_length?: number
}

export type GtmEvent =
  | QuizLandedEvent
  | QuizStartedEvent
  | QuizScreenViewedEvent
  | QuizQuestionAnsweredEvent
  | QuizCompletedEvent
  | SignUpEvent
  | UserClickEvent
  | VideoEvent

// =====================================================================
// Helpers
// =====================================================================

const PLATFORM_NUTRITION_SITE: Platform = 'nutrition_site'
const SECTION_QUIZ: Section = 'quiz'

const getQuizCookies = () => document.cookie.split('; ')

const getCookieValue = (cookies: string[], name: string) =>
  cookies.find((row) => row.startsWith(name + '='))?.split('=')[1]

const baseEvent = (quizVersion: string, productType: ProductType, userId?: string) => {
  const cookies = getQuizCookies()
  return {
    platform: PLATFORM_NUTRITION_SITE,
    section: SECTION_QUIZ,
    user_id: userId ?? getCookieValue(cookies, 'cUserId'),
    quiz_version: quizVersion,
    product_type: productType,
  }
}

const sendGtmEvent = (event: GtmEvent) => {
  TagManager.dataLayer({
    dataLayer: event,
    dataLayerName: GTM_ZOE_DATALAYER,
  })
}

// =====================================================================
// Trackers
// =====================================================================

export const trackQuizLanded = (quizVersion: string, productType: ProductType, userId?: string): void =>
  sendGtmEvent({
    ...baseEvent(quizVersion, productType, userId),
    event: 'quiz_landed',
    link_position: 'page_body',
  })

export const trackQuizStarted = (quizVersion: string, productType: ProductType): void =>
  sendGtmEvent({
    ...baseEvent(quizVersion, productType),
    event: 'quiz_started',
    link_position: 'page_body',
  })

export const trackQuizScreenViewed = (quizVersion: string, productType: ProductType, questionName: string): void =>
  sendGtmEvent({
    ...baseEvent(quizVersion, productType),
    event: 'quiz_screen_viewed',
    question_name: questionName,
  })

export const trackQuizQuestionAnswered = (
  quizVersion: string,
  productType: ProductType,
  questionName: string,
  questionAnswer: string | number,
): void =>
  sendGtmEvent({
    ...baseEvent(quizVersion, productType),
    event: 'quiz_question_answered',
    question_name: questionName,
    question_answer: questionAnswer,
  })

export const trackQuizCompleted = (quizVersion: string, productType: ProductType): void =>
  sendGtmEvent({
    ...baseEvent(quizVersion, productType),
    event: 'quiz_completed',
  })

/** Fire quiz_completed and wait for GTM to process before calling onComplete.
 *  Falls back to a 500ms timeout in case GTM is blocked or slow. */
export const trackQuizCompletedWithCallback = (
  quizVersion: string,
  productType: ProductType,
  onComplete: () => void,
): void => {
  let hasFired = false
  const done = () => {
    if (hasFired) return
    hasFired = true
    onComplete()
  }

  TagManager.dataLayer({
    dataLayer: {
      ...baseEvent(quizVersion, productType),
      event: 'quiz_completed',
      eventCallback: done,
    },
    dataLayerName: GTM_ZOE_DATALAYER,
  })

  // Fallback in case GTM is blocked (e.g. ad blocker) or eventCallback isn't triggered
  setTimeout(done, 500)
}

export const trackQuizSignUp = (
  quizVersion: string,
  productType: ProductType,
  emailList: EmailListType,
  email: string,
  marketingConsent: MarketingConsent,
  tag?: string,
): void =>
  sendGtmEvent({
    ...baseEvent(quizVersion, productType),
    event: 'sign_up',
    email_list: emailList,
    hashed_email: email.trim() ? sha256(email.toLowerCase().trim()) : undefined,
    marketing_consent: marketingConsent,
    link_position: 'page_body',
    tag,
  })

export const trackQuizUserClick = (
  quizVersion: string,
  productType: ProductType,
  clickType: ClickType,
  options?: { linkTextImage?: string; pageName?: string; linkPosition?: LinkPosition },
): void =>
  sendGtmEvent({
    ...baseEvent(quizVersion, productType),
    event: 'user_click',
    click_type: clickType,
    link_position: options?.linkPosition ?? 'page_body',
    link_text_image: options?.linkTextImage,
    page_name: options?.pageName,
  })

export const trackQuizVideoEvent = (
  quizVersion: string,
  productType: ProductType,
  videoName: string,
  videoAction: VideoAction,
  videoLength?: number,
): void =>
  sendGtmEvent({
    ...baseEvent(quizVersion, productType),
    event: 'video_action',
    video_action: videoAction,
    video_name: videoName,
    video_length: videoLength,
  })
